import { faSpinnerThird } from '@fortawesome/sharp-solid-svg-icons';
import Link from 'next/link';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { SelectField } from 'src/components/Form/SelectField';
import { TextField } from 'src/components/Form/TextField';
import { Text } from 'src/components/ui/Text';
import { useRequest } from 'src/hooks/useRequest';
import { getCities, getStates, getZipCode } from 'src/services/PlatformAPI';
import { OrderAddressType, OrderType } from 'src/types';
import { AddressFromZipcodeType } from 'src/types/Address';
import { cn } from 'src/utils/tailwind';
import { Icon } from '../ui/Icon';

const FORBIDDEN_ADDRESS_KEYS = ['zipcode', 'city', 'state'];

type AddressFormProps = {
  order?: OrderType;
  onAddressLoad?: () => void;
};

const AddressForm = (props: AddressFormProps) => {
  const stateId = props.order?.address?.city?.state?.id;
  const [showAddressForm, setShowAddressForm] = useState(Boolean(stateId));
  const { getValues, setValue, setFocus, watch } = useFormContext();
  const zipCode = watch('zipCode');

  const {
    isLoading: isLoadingZipCode,
    executeRequest: fetchZipCode,
    data: address,
  } = useRequest<AddressFromZipcodeType>(getZipCode, {
    timeout: 10000,
    onSuccess: (address) => {
      setAddressFormValues(address);
      fetchCities(address.stateId);
    },
    onFinally: () => {
      setShowAddressForm(true);
      props.onAddressLoad && props.onAddressLoad();
    },
  });

  const { data: states } = useRequest(getStates, {
    enabled: true,
  });

  const { data: cities, executeRequest: fetchCities } = useRequest(getCities, {
    initialData: stateId,
    enabled: Boolean(stateId),
  });

  const onStateChange = (stateId) => {
    setValue('cityId', '');
    fetchCities(stateId);
  };

  const searchZipCode = (zipCode: string) => {
    if (!zipCode || zipCode.length < 9) return;

    fetchZipCode(zipCode);
  };

  const setAddressFormValues = (address: AddressFromZipcodeType) => {
    Object.keys(address).forEach((key) => {
      const value = address[key as keyof typeof address] || getValues(key);

      if (FORBIDDEN_ADDRESS_KEYS.includes(key) || !value) return;

      setValue(key, value, { shouldValidate: true });
    });

    setFocus('number');
  };

  const statesOptions = (states || []).map((state) => ({
    label: state.acronym,
    value: state.id,
  }));
  const citiesOptions = (cities || []).map((city) => ({
    label: city.name,
    value: city.id,
  }));
  const hasAddressFound = Boolean(address?.street || props.order?.address);
  const formattedAddress = props.order
    ? formatAddressFromOrder(props.order.address)
    : formatAddressFromZipcode(address);

  return (
    <>
      <div className="mt-4 grid grid-cols-4 gap-4 sm:gap-x-8">
        <div className="col-span-2 sm:col-span-1">
          <TextField
            name="zipCode"
            label="CEP"
            type="tel"
            mask="zipCode"
            disabled={isLoadingZipCode}
            onChange={(e) => searchZipCode(e.target.value)}
            isRequired
          />
        </div>

        {isLoadingZipCode && (
          <div
            className="col-span-1 flex items-center pt-4"
            data-testid="address-loading"
          >
            <Icon
              size="lg"
              icon={faSpinnerThird}
              className="animate-spin text-accent-main"
            />
          </div>
        )}

        <div
          className={cn('col-span-2 pt-4', {
            hidden: zipCode && zipCode.length === 9,
          })}
        >
          <Text>Não sabe?</Text>
          <Link
            className="text-accent-main underline hover:text-accent-most"
            href="https://buscacepinter.correios.com.br/app/endereco/index.php"
            target="_blank"
          >
            Procurar meu CEP
          </Link>
        </div>
      </div>
      <div
        data-testid="hidden-address-form"
        className={cn('mt-4 grid grid-cols-4 gap-4 sm:gap-x-8', {
          hidden: !showAddressForm,
        })}
      >
        {!hasAddressFound && (
          <>
            <div className="col-span-4 sm:col-span-2">
              <SelectField
                name="stateId"
                label="UF"
                onChange={(e) => onStateChange(e.target.value)}
                options={statesOptions}
                placeholder=" "
                isRequired
                disabled={isLoadingZipCode}
              />
            </div>

            <div className="col-span-full sm:col-span-2">
              <SelectField
                name="cityId"
                label="Cidade"
                options={citiesOptions}
                placeholder=" "
                isRequired
                disabled={isLoadingZipCode}
              />
            </div>

            <div className="col-span-full sm:col-span-2">
              <TextField
                name="neighborhood"
                label="Bairro"
                isRequired
                disabled={isLoadingZipCode}
              />
            </div>

            <div className="col-span-full sm:col-span-3">
              <TextField
                name="street"
                label="Logradouro"
                isRequired
                disabled={isLoadingZipCode}
              />
            </div>
          </>
        )}

        {hasAddressFound && (
          <Text
            weight="bold"
            data-testid="formatted-address"
            className="col-span-full"
          >
            {formattedAddress}
          </Text>
        )}

        <div className="col-span-4 sm:col-span-1">
          <TextField
            name="number"
            label="Número"
            type="tel"
            isRequired
          />
        </div>

        <div className={cn('col-span-full', { 'sm:col-span-3': hasAddressFound })}>
          <TextField
            name="complement"
            label="Complemento"
            detail="Opcional"
          />
        </div>
      </div>
    </>
  );
};

const formatAddressFromOrder = (address: OrderAddressType) => {
  if (!address) return '';
  return `${address.street}, ${address.neighborhood} - ${address.city.name} - ${address.city.state.name}`;
};

const formatAddressFromZipcode = (address?: AddressFromZipcodeType) => {
  if (!address || !address?.street) return '';
  return `${address.street}, ${address.neighborhood} - ${address.city} - ${address.state}`;
};

export { AddressForm };
